/**
 * materials.json schema: faction palettes (style guide §5.4 is the source of truth for the values),
 * global material constants, emissive bands (style guide §3.6) and the hull surface recipe (asset contract
 * §4.5): trim fallbacks, panel lines, wear, soot, damage, decals, per-preset tiers and tuning defaults.
 * Asset-lane owned (A5). A11 reads `factions.*.engine*`, `sensor` and `emissive.*`: those keys are stable.
 */
import { z } from 'zod'
import type { MaterialSlot } from '../../data/assetSchema'
import type { FactionId, QualityPreset } from '../contracts'
import materialsJson from './materials.json'

/** assetSchema MATERIAL_SLOTS, restated: this file stays loadable under plain Node (tools/assetkit/style/palette.ts). */
const SLOTS = ['hull', 'glass', 'emissive', 'decal', 'set'] as const satisfies readonly MaterialSlot[]
const hex = z.string().regex(/^#[0-9A-Fa-f]{6}$/)
const unit = z.number().min(0).max(1)
const rough = z.number().min(0.18).max(1) // style guide §4: roughness floor 0.18 everywhere
const range = z.tuple([z.number().nonnegative(), z.number().positive()]).refine((r) => r[1] > r[0], { message: 'range end must exceed its start' })

export const factionPaletteSchema = z.object({
  paintPrimary: hex,
  paintSecondary: hex,
  paintAccent: hex,
  metalTint: hex,
  primer: hex,
  decalInk: hex,
  decalInkAlt: hex,
  hazardA: hex,
  hazardB: hex,
  engineCore: hex,
  engineHalo: hex,
  sensor: hex,
  grime: hex,
  wear: unit,
  grimeAmount: unit,
  sootAmount: unit,
  /** Roughness of the primary, secondary and accent paint (style guide §4: 0.38–0.55; chalked paint up to 0.75). */
  paintRoughness: z.tuple([rough, rough, rough]),
  /** Roughness of the bare-metal zone. */
  metalRoughness: rough,
  /** Oxide on bare metal, 0..1: metalness of bare metal = 1 − 0.75 × oxidation (first_rush oxidised alloy). */
  oxidation: unit,
  /** Share of the High-preset clearcoat this livery keeps (maintained enamel 1; wrecker overspray and chalked paint low). */
  gloss: unit,
  /** Sun bleaching of up-facing paint, 0..1 (style guide §8.1: up to 30% desaturation on old hulls). */
  bleach: unit,
  /** Micrometeoroid pitting on forward faces, 0..1 (style guide §8.1). */
  pitting: unit,
  /** Canopy glass tint (style guide §4: #0B141B–#16222C; raider smoked #1A1416). */
  glassTint: hex,
  /** E0 glow behind canopy glass (Kestrel instruments: amber Y 0.3); Y 0 = dark glass. */
  glassGlow: hex,
  glassGlowY: z.number().min(0).max(0.9),
  /** MAT_emissive texels near no ENG_ or LIGHT_ socket (lit windows, status lamps). */
  emissiveIdle: hex,
  emissiveIdleY: z.number().min(0).max(5),
  /**
   * Chip look of the edge wear (style guide §8.1, §8.3). Absent: the legacy response (chips reveal `primer`, raw
   * ECL_Mask G, `wear.metalDepth`). Present: chips reveal a per-zone under-layer, through a contrast threshold on G.
   */
  chips: z
    .object({
      /** Colour under chipped primary, secondary and accent paint (raider: Varro gunmetal under the red overpaint). */
      under: z.tuple([hex, hex, hex]),
      /** G is remapped through smoothstep(lo, hi) so a soft baked band chips in islands instead of washing out. */
      contrast: z.tuple([unit, unit]).refine((r) => r[1] > r[0], { message: 'contrast hi must exceed lo' }),
      /** Peak of the contrasted mask: below 1 the chip noise decides, so only part of each edge run chips. */
      edge: z.number().min(0.2).max(1.5),
      /** Score depth past the chip front where bare metal shows (only the deepest chips reach metal). */
      bareDepth: z.number().min(0).max(1),
      /** Chip size × the class-scaled chip noise (> 1: larger chips). */
      size: z.number().min(0.25).max(4),
      /** Weight of the low-frequency large-chip octave (5–15 cm chips at fighter class), 0..1. */
      large: unit,
    })
    .optional(),
  /** Low-frequency value break-up of large paint areas (faded, resprayed patches). Absent: off. */
  tonal: z
    .object({
      /** Value drop in the darkest patches, 0..0.25. */
      amount: z.number().min(0).max(0.25),
      /** Desaturation in those patches, 0..0.5. */
      desaturate: z.number().min(0).max(0.5),
      /** Patch frequency per metre at fighter class (scaled by the class panel pitch like the other wear). */
      frequency: z.number().positive().max(2),
    })
    .optional(),
})

const bandClassSchema = z.object({ albedo: hex, metalness: unit, roughness: rough })

const panelClassSchema = z.object({
  /** Applies to models whose bounding radius (metres) is at most this. */
  maxRadius: z.number().positive(),
  /** Primary and secondary panel pitch in metres (style guide §6.2). */
  primary: z.number().positive(),
  secondary: z.number().positive(),
  /** Seam width in metres. */
  seamWidth: z.number().positive(),
})

const presetTierSchema = z.object({
  /** Octaves of object-space noise for wear, grime and roughness breakup; 0 = masks and trims only. */
  noiseOctaves: z.number().int().min(0).max(4),
  clearcoat: z.boolean(),
  panelLines: z.boolean(),
  secondaryPanels: z.boolean(),
  detailNormal: z.boolean(),
  soot: z.boolean(),
  heat: z.boolean(),
  pitting: z.boolean(),
  cracks: z.boolean(),
  anisotropy: z.number().int().min(1).max(16),
  /**
   * L-M4 U1 (D-924, D-802 switch): dequantize KHR_mesh_quantization GLBs at load so their mesh-local space is metres and
   * hit scorch, soot, heat, seams, the drive band and lens colours size right. Read when models load (boot preset).
   */
  dequantize: z.boolean().default(true),
})

export const materialsFileSchema = z.object({
  factions: z.object({
    player_lease: factionPaletteSchema,
    raider: factionPaletteSchema,
    first_rush: factionPaletteSchema,
    neutral: factionPaletteSchema,
    authority: factionPaletteSchema,
    tethys: factionPaletteSchema,
    meridian: factionPaletteSchema,
    varro_security: factionPaletteSchema,
    mutual: factionPaletteSchema,
    holdfast: factionPaletteSchema,
    guardian: factionPaletteSchema,
    /** The player corporation (M6 P23): the base look; `CorpRecord.livery` replaces the three paint zones (setCorpLivery). */
    player_corp: factionPaletteSchema,
  }),
  globals: z.object({
    soot: hex,
    rubber: hex,
    radiator: hex,
    glassTint: hex,
    navPort: hex,
    navStbd: hex,
    navWhite: hex,
    rimColor: hex,
    rimStrength: z.number().min(0).max(1),
    rimPower: z.number().positive(),
    bareMetalRoughness: rough,
    primerRoughness: rough,
    /** Nozzle heat gradient from the throat: straw, bronze, blue, then cooled grey (style guide §4). */
    heatRamp: z.tuple([hex, hex, hex, hex]),
    /** Paint baked matte brown near engine bells (style guide §8.1 thermal). */
    bakedPaint: hex,
    scorch: hex,
    crackGlow: hex,
    lensOff: hex,
    hullEnvIntensity: z.number().min(0).max(2),
    glassEnvIntensity: z.number().min(0).max(2),
    glassRoughness: z.number().min(0.02).max(0.1),
    glassIor: z.number().min(1).max(2),
    /** Station windows (glass without a pilot eye socket): share of window cells lit and the cell size (m). */
    windowLitShare: z.number().min(0).max(1),
    windowCell: z.number().positive(),
  }),
  emissive: z.object({
    engineIdleY: z.number().nonnegative(),
    engineFullY: z.number().positive(),
    boostMult: z.number().min(1),
    signalY: z.number().positive(),
    strobePeakY: z.number().positive(),
    /** Lens of a blinking light between flashes (the sprite does the flash, A11). */
    blinkLensY: z.number().nonnegative(),
    /** MAT_emissive texels within this many metres of a LIGHT_ socket take its colour. */
    lightLensReach: z.number().positive(),
    /** ... within this many nozzle radii of an ENG_ socket are that nozzle's throat. */
    engineLensReach: z.number().positive(),
    /** Nozzles below this radius (metres) are auxiliaries at E2 × auxMult (interceptor sheet). */
    auxRadius: z.number().nonnegative(),
    auxMult: unit,
  }),
  trim: z.object({
    /** Sets whose largest map is smaller than this are carriers (P0: 4 × 4): the procedural fallback is used. */
    authoredMinPx: z.number().int().positive(),
    /** Linear albedo of a paintable trim texel that leaves the paint unchanged (paint × albedo / neutral). */
    neutralAlbedo: z.number().positive().max(1),
    /** Roughness of a paintable trim texel that leaves the zone roughness unchanged. */
    neutralRoughness: rough,
    /** tex.detail.metal is sampled at UV0 × this (asset contract §4.5). */
    detailTiling: z.number().positive(),
    detailFade: range,
    detailStrength: z.number().min(0).max(2),
    /**
     * Whether hero hulls (baked ECL_Mask + trim-sheet UVs, geometryTraits baked and trimmed) also get the procedural
     * plate layer: the plate tone and roughness variation, the age mottle and the seam lattice. Off (D-939): the baked
     * trim panels carry their own panel lines, and the class-pitch seam grid on top read as doubled lines. Every other
     * variant (the f32 placeholders) always keeps plates.
     */
    proceduralPlates: z.boolean(),
    /** Procedural PBR per unpainted band id of trim-layout.json (fallback when the trim set is a carrier). */
    bandClasses: z.record(z.string(), bandClassSchema),
  }),
  panelLines: z.object({
    classes: z.array(panelClassSchema).min(1),
    /** Distance fade (metres) of the procedural seams. */
    fade: range,
    /** Albedo darkening inside a seam, 0..1. */
    darken: unit,
    /** Share of lattice cells that hold a secondary access panel (High). */
    secondaryShare: unit,
    /** Seams fade out where the edge mask rises through this range (they stop short of bevels). */
    edgeClear: z.tuple([unit, unit]),
  }),
  wear: z.object({
    /** Object-space frequency (1/m) of the chip noise; fighter chips are 5–40 mm (style guide §8.3). */
    chipFrequency: z.number().positive(),
    /** High-frequency wear fades to its average between these distances (metres) ... */
    fade: range,
    /** ... and between these pixel footprints (metres per pixel at fighter scale). */
    footprintFade: range,
    /** Fallback edge bake (models without ECL_Mask.g/.b): wear and grime band widths in metres. */
    edgeWidth: z.number().positive(),
    cavityWidth: z.number().positive(),
    /** Minimum dihedral angle (degrees) for the fallback bake to call an edge a feature. */
    featureAngleDeg: z.number().positive().max(90),
    /** Extra reveal depth from primer to bare metal. */
    metalDepth: z.number().positive().max(1),
    grimeFrequency: z.number().positive(),
    breakupFrequency: z.number().positive(),
    /** Roughness breakup amplitude (style guide §4: ±0.08). */
    breakup: z.number().min(0).max(0.2),
    /** Per-plate variation of painted panels: value (±) and roughness (±), so flat hulls read as assembled plates. */
    plateValue: z.number().min(0).max(0.2),
    plateRoughness: z.number().min(0).max(0.2),
    /** Large-scale age mottling of paint (±value) and its frequency per metre, scaled by the class panel pitch. */
    mottle: z.number().min(0).max(0.3),
    mottleFrequency: z.number().positive(),
  }),
  soot: z.object({
    /** Main and retro nozzles: downstream soot fan reach and lip ring reach in nozzle radii; fan widening per metre. */
    nozzleReach: z.number().positive(),
    lipReach: z.number().positive(),
    fanSpread: z.number().nonnegative(),
    /** RCS ports: soot reach in metres. */
    rcsReach: z.number().positive(),
    /** Heat tint reach in nozzle radii. */
    heatReach: z.number().positive(),
    streakFrequency: z.number().positive(),
  }),
  damage: z.object({
    scorchRadius: z.number().positive(),
    crackRadius: z.number().positive(),
    crackGlowY: z.number().positive(),
    /** Crack glow half-life in seconds after a hit. */
    crackHalfLife: z.number().positive(),
    /** Damage above this adds procedural scorch patches even without recorded hits. */
    wornThreshold: unit,
  }),
  decals: z.object({
    /** Threshold distortion of hand-painted cells (atlas G = 0; style guide §7.7 operator art). */
    distortion: z.number().min(0).max(0.25),
    roughnessOffset: z.number().min(0).max(0.2),
  }),
  shadows: z.object({
    /**
     * Ship and module slots that cast key-light shadows (PERF PF-4, D-771; global, every preset). `["hull"]`: glass is
     * opaque and emissive lenses sit ≤ 2 cm proud of the hull, so their shadows are under a pixel in chase view.
     * `["hull", "glass", "emissive"]` restores the pre-PF-4 casters (decals never cast). Stations, structures and the
     * derelict are unaffected: `enableShadows` re-enables their opaque meshes after the look is applied.
     */
    casterSlots: z.array(z.enum(SLOTS)).min(1),
    /**
     * L-M4 C1 (D-910, D-922; global, every preset, value-identical so D-802 allows one flag): the hull and glass
     * colour graphs build as a constant in three's shadow override material. three multiplies the shadow alpha by
     * `colorNode.a`, which is the constant 1.0 on these vec3 colours, but still built the albedo graph and updated its
     * ~40 per-object uniforms on every caster render. `false` restores the old shadow pipelines.
     */
    litOnlyColor: z.boolean(),
  }),
  lights: z.object({
    /**
     * PERF PF-5b (D-TBD PERF-8): ship views draw their hull LIGHT_ sockets from one instanced pool (`createLightPool`)
     * instead of one sprite per light. false = per-light sprites (the pre-PF-5b path). Stations, hardpoint modules,
     * wrecks, torpedoes and the derelict always keep sprites (`attachShipLights`, `createLightSprite`).
     */
    pooled: z.boolean(),
    /** Pool capacity in lights (every live ship view's hull lights); a full pool drops further lights with one warning. */
    poolCapacity: z.number().int().min(1).max(65536),
    /**
     * Scale W4 light-pool (D-TBD): static models (stations, structures, props, mount modules, wreck parts) draw all
     * `LIGHT_` sockets of one instance in one instanced draw (`createStaticLightPool`: root-space positions, no
     * per-frame work, the same per-light look) instead of one sprite each. false = per-light sprites. The derelict keeps
     * sprites either way.
     */
    staticPooled: z.boolean(),
  }),
  plumes: z.object({
    /**
     * PERF L-M4 (D-TBD): the additive, depth-write-off plume halo and core shells draw both faces in one pass
     * (`forceSinglePass`) instead of three's two-pass back-then-front transparent path: additive blending is
     * order-independent, so the picture is the same and each shell costs one draw instead of two. false = the two-pass
     * path. The heat shimmer (normal blending) always keeps two passes.
     */
    singlePassShells: z.boolean(),
    /**
     * PERF L-M4 part 3 (D-TBD): ship views draw every plume's halo and core shells and nozzle glow from one instanced
     * pool per family (`createPlumePool`: three draws for every faction) instead of three objects per plume. false =
     * per-plume meshes and sprites (part 1's path). Torpedoes, wrecks and lookdev keep per-plume meshes; the heat
     * shimmer always stays per plume.
     */
    pooled: z.boolean(),
    /** Pool capacity in plumes (every live ship view's ENG_MAIN_/ENG_RETRO_ sockets); a full pool drops further plumes with one warning. */
    poolCapacity: z.number().int().min(1).max(65536),
  }),
  presets: z.object({ low: presetTierSchema, medium: presetTierSchema, high: presetTierSchema }),
  /** G5 (PERF, D-TBD): value-identical shader trims, each switchable for A/B. */
  shaderPerf: z.object({
    /** Hull soot/heat, damage and emissive loops stop at the object's engine, hit and light counts (objectData `counts`). False = all 8 slots every pixel (pre-G5). */
    boundSlotLoops: z.boolean(),
  }),
  tuning: z.object({
    wear: z.number().min(0).max(3),
    grime: z.number().min(0).max(3),
    soot: z.number().min(0).max(3),
    rim: z.number().min(0).max(3),
    clearcoat: unit,
    clearcoatRoughness: z.number().min(0.05).max(1),
    panelLines: z.number().min(0).max(3),
  }),
})

export type FactionPalette = z.infer<typeof factionPaletteSchema>
export type MaterialsFile = z.infer<typeof materialsFileSchema>
export type PresetTier = z.infer<typeof presetTierSchema>
export type PanelClass = z.infer<typeof panelClassSchema>
export type TuningDefaults = MaterialsFile['tuning']

export function parseMaterialsData(json: unknown): MaterialsFile {
  return materialsFileSchema.parse(json)
}

export function loadMaterialsData(): MaterialsFile {
  return parseMaterialsData(materialsJson)
}

export function paletteOf(data: MaterialsFile, faction: FactionId): FactionPalette {
  return data.factions[faction]
}

/** The edge-wear look the hull shader builds for a livery; the defaults reproduce the legacy response exactly. */
export interface WearLook {
  /** Under-layer per paint zone (primary, secondary, accent), or null: the `uPrimer` uniform everywhere. */
  readonly under: readonly [string, string, string] | null
  /** Contrast threshold on ECL_Mask G, or null: raw G. */
  readonly contrast: readonly [number, number] | null
  /** Gain on the contrasted mask (1 = legacy). */
  readonly edge: number
  readonly bareDepth: number
  /** Chip noise frequency multiplier (1 / chips.size). */
  readonly chipFreqMul: number
  readonly large: number
  /** Tonal break-up; amount 0 = off (no nodes built). */
  readonly tonal: { readonly amount: number; readonly desaturate: number; readonly frequency: number }
}

/** Large chips use this fraction of the chip frequency (≈ 3× the chip size). */
export const LARGE_CHIP_FREQ = 0.3

export function wearLookOf(data: MaterialsFile, faction: FactionId): WearLook {
  return wearLookOfPalette(paletteOf(data, faction), data.wear.metalDepth)
}

/** The wear look of a palette; `metalDepth` is the legacy bare-metal depth (materials.json `wear.metalDepth`). */
export function wearLookOfPalette(p: FactionPalette, metalDepth: number): WearLook {
  const c = p.chips
  return {
    under: c ? c.under : null,
    contrast: c ? c.contrast : null,
    edge: c ? c.edge : 1,
    bareDepth: c ? c.bareDepth : metalDepth,
    chipFreqMul: c ? 1 / c.size : 1,
    large: c ? c.large : 0,
    tonal: p.tonal ?? { amount: 0, desaturate: 0, frequency: 1 },
  }
}

/**
 * CPU mirror of the shader's chip layers at one texel (tests and tuning): the under-layer and bare-metal coverage
 * for a baked edge mask `g`, chip noise `n` (0..1, 0.5 = mean), edge-run noise `runs` and the livery wear.
 */
export function chipLayers(look: WearLook, g: number, n: number, runs: number, wear: number): { under: number; bare: number } {
  const ss = (a: number, b: number, x: number): number => {
    const t = Math.min(1, Math.max(0, (x - a) / (b - a)))
    return t * t * (3 - 2 * t)
  }
  const e = (look.contrast ? ss(look.contrast[0], look.contrast[1], g) : g) * look.edge
  const cause = e * (runs * 1.1 + 0.35)
  const score = cause + (n - 0.5) * 0.9 * Math.min(1, cause * 3)
  const threshold = 1.05 - Math.min(1, wear) * 0.95
  const aa = 0.015
  return { under: ss(threshold - aa, threshold + aa, score), bare: ss(threshold + look.bareDepth - aa, threshold + look.bareDepth + aa, score) }
}

export function tierOf(data: MaterialsFile, preset: QualityPreset): PresetTier {
  return data.presets[preset]
}

/**
 * The panel class (style guide §6.2 fighter / gunship / station) for a model of the given bounding radius.
 * Statics (applyStaticLook) larger than a fighter are station modules; small statics (hardpoints) ride on fighters.
 */
export function panelClassFor(data: MaterialsFile, radius: number, isStatic = false): PanelClass {
  const classes = data.panelLines.classes
  if (isStatic && radius > classes[0].maxRadius) return classes[classes.length - 1]
  for (let i = 0; i < classes.length; i++) if (radius <= classes[i].maxRadius) return classes[i]
  return classes[classes.length - 1]
}
